/**
 * "Add Wisdom" capture panel (wisdom_feature_spec.md) — shown directly
 * below the SQL Investigation resultset. Captures per-table purpose /
 * how-to-fetch prose (+ up to 5 sample rows from the on-screen result)
 * and optional intent rules, in under 30 seconds. Admin-only; upserts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useIsAdmin } from '../../auth/permissions';
import { useAuthStore } from '../../auth/authStore';
import { wisdomApi, TableWisdom, IntentWisdom } from '../../services/wisdomApi';

/** All 3-part FQNs referenced by the SQL (FROM + JOINs), first = primary. */
function tablesFromSql(sql: string): string[] {
  const re = /\b(?:from|join)\s+([A-Za-z0-9_\[\]]+\.[A-Za-z0-9_\[\]]+\.[A-Za-z0-9_\[\]]+)/gi;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(sql)) !== null) {
    const fqn = m[1].replace(/[\[\]]/g, '');
    if (!out.some((t) => t.toLowerCase() === fqn.toLowerCase())) out.push(fqn);
  }
  return out;
}

const splitFqn = (fqn: string) => {
  const parts = fqn.split('.');
  return { dbname: parts[0] ?? '', table: parts[parts.length - 1] ?? '' };
};

export function WisdomPanel({ sql, rows }: { sql: string; rows: Record<string, unknown>[] }) {
  const isAdmin = useIsAdmin();
  const email = useAuthStore((s) => s.user?.email ?? null);
  const [open, setOpen] = useState(false);
  const tables = useMemo(() => tablesFromSql(sql), [sql]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [purpose, setPurpose] = useState('');
  const [howToFetch, setHowToFetch] = useState('');
  const [attachRows, setAttachRows] = useState(true);
  const [intentOpen, setIntentOpen] = useState(false);
  const [intents, setIntents] = useState<IntentWisdom[]>([]);
  const [intentName, setIntentName] = useState('');
  const [mustHave, setMustHave] = useState('');
  const [typicalRows, setTypicalRows] = useState('');
  const [useAsExample, setUseAsExample] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null);

  // Default selection: the primary FROM table. Reset per new query.
  useEffect(() => {
    setSelected(new Set(tables.slice(0, 1)));
  }, [tables]);
  useEffect(() => {
    setAttachRows(rows.length > 0);
  }, [rows.length]);

  // Prefill from existing wisdom when exactly one table is selected.
  useEffect(() => {
    if (!open || selected.size !== 1) return;
    const fqn = Array.from(selected)[0];
    let cancelled = false;
    wisdomApi
      .getForTables([fqn])
      .then((ws) => {
        if (cancelled || ws.length === 0) return;
        setPurpose(ws[0].purpose ?? '');
        setHowToFetch(ws[0].how_to_fetch ?? '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, selected]);

  useEffect(() => {
    if (!open || !intentOpen || intents.length > 0) return;
    wisdomApi.listIntents().then(setIntents).catch(() => {});
  }, [open, intentOpen, intents.length]);

  if (!isAdmin || tables.length === 0) return null;

  const save = async () => {
    setSaving(true);
    setToast(null);
    try {
      const sample = attachRows && rows.length > 0 ? rows.slice(0, 5) : null;
      const columns = sample ? Object.keys(sample[0] ?? {}) : null;
      for (const fqn of Array.from(selected)) {
        const { dbname, table } = splitFqn(fqn);
        const w: TableWisdom = {
          dbname,
          table_name: table,
          purpose: purpose.trim() || null,
          how_to_fetch: howToFetch.trim() || null,
          sample_rows: sample,
          columns_snapshot: columns,
        };
        await wisdomApi.saveTable(w);
      }
      if (intentName.trim()) {
        await wisdomApi.saveIntent({
          intent: intentName.trim(),
          must_have: mustHave.trim() || null,
          typical_rows: typicalRows.trim() || null,
          example_sql: useAsExample ? sql : null,
          notes: null,
        });
      }
      setToast({ ok: true, text: `Wisdom saved for ${selected.size} table(s)${intentName.trim() ? ' + intent' : ''}` });
    } catch (e) {
      setToast({ ok: false, text: e instanceof Error ? e.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 border border-amber-200 bg-amber-50/40 rounded">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-amber-800"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Add Wisdom
        <span className="font-normal text-amber-600">— teach LISA about {tables[0]}{tables.length > 1 ? ` (+${tables.length - 1})` : ''}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2 text-[11px]">
          <div className="flex flex-wrap gap-2">
            {tables.map((fqn) => (
              <label key={fqn} className="inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.has(fqn)}
                  onChange={() =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(fqn)) next.delete(fqn); else next.add(fqn);
                      return next;
                    })
                  }
                />
                <span className="font-mono">{fqn}</span>
              </label>
            ))}
          </div>
          <input
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Purpose — what this table holds (1–2 lines)"
            className="w-full border border-gray-300 rounded px-2 py-1"
          />
          <textarea
            value={howToFetch}
            onChange={(e) => setHowToFetch(e.target.value)}
            placeholder="How to fetch — key lookup columns, standard joins, required filters, traps"
            rows={4}
            className="w-full border border-gray-300 rounded px-2 py-1 font-mono text-[10px]"
          />
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={attachRows} disabled={rows.length === 0} onChange={(e) => setAttachRows(e.target.checked)} />
            Attach sample rows ({Math.min(rows.length, 5)} of {rows.length}; stays internal)
          </label>
          <div>
            <button onClick={() => setIntentOpen(!intentOpen)} className="flex items-center gap-1 text-amber-800 font-medium">
              {intentOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />} Intent (optional)
            </button>
            {intentOpen && (
              <div className="mt-1 space-y-1.5 pl-3 border-l-2 border-amber-200">
                <input
                  type="text"
                  list="wisdom-intents"
                  value={intentName}
                  onChange={(e) => setIntentName(e.target.value)}
                  placeholder="Intent name, e.g. rate_guideline"
                  className="w-full border border-gray-300 rounded px-2 py-1"
                />
                <datalist id="wisdom-intents">
                  {intents.map((i) => <option key={i.intent} value={i.intent} />)}
                </datalist>
                <input
                  type="text"
                  value={mustHave}
                  onChange={(e) => setMustHave(e.target.value)}
                  placeholder="Must-have predicates, e.g. POL and POD; equipment type narrows"
                  className="w-full border border-gray-300 rounded px-2 py-1"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={typicalRows}
                    onChange={(e) => setTypicalRows(e.target.value)}
                    placeholder="Typical rows, e.g. 1-20"
                    className="w-28 border border-gray-300 rounded px-2 py-1"
                  />
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={useAsExample} onChange={(e) => setUseAsExample(e.target.checked)} />
                    Use this query as example
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving || selected.size === 0 || (!purpose.trim() && !howToFetch.trim() && !intentName.trim())}
              className="px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 flex items-center gap-1"
            >
              {saving && <Loader2 size={11} className="animate-spin" />} Save
            </button>
            {toast && (
              <span className={toast.ok ? 'text-green-700' : 'text-red-700'}>{toast.text}</span>
            )}
            {email && <span className="text-gray-400 ml-auto">as {email}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
